import React, { useState } from "react";
import { Button, Table, Dropdown, Modal } from "antd";
import { EllipsisOutlined } from "@ant-design/icons";
import { ReactComponent as AndroidIcon } from "../../assets/android.svg";
import { ReactComponent as AppleIcon } from "../../assets/apple.svg";
import plusIcon from "../../assets/plus.svg";
import TrafficSourceModal from "./TrafficSourceModal";
import { useMutation, useQuery } from "@apollo/client";
import {
  ADD_TRAFFIC_SOURCES,
  DELETE_TRAFFIC_SOURCES,
  EDIT_TRAFFIC_SOURCES,
  GET_TRAFFIC_SOURCES,
} from "../../api/graphql/orders";
import { TrafficSource } from "./types";
import BasicButton from "../../BasicButton";
import Page from "../Page";
import styles from "./styles.module.scss";
import dialog from "../../styles/dialog.module.scss";

const TrafficSourcesComponent: React.FC = () => {
  const { data, loading, refetch } = useQuery(GET_TRAFFIC_SOURCES);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [currentSource, setCurrentSource] = useState<
    TrafficSource | undefined
  >();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [currentDeleteSourceId, setCurrentDeleteSourceId] = useState<
    string | null
  >(null);

  const [addTrafficSource] = useMutation(ADD_TRAFFIC_SOURCES);
  const [editTrafficSource] = useMutation(EDIT_TRAFFIC_SOURCES);
  const [deleteTrafficSource] = useMutation(DELETE_TRAFFIC_SOURCES);

  const handleAdd = () => {
    setModalMode("add");
    setCurrentSource(undefined);
    setModalOpen(true);
  };

  const handleEdit = (source: TrafficSource) => {
    setModalMode("edit");
    setCurrentSource(source);
    setModalOpen(true);
  };

  const handleSave = async (source: TrafficSource) => {
    if (modalMode === "add") {
      await addTrafficSource({
        variables: {
          trafficSource: source,
        },
      });
    } else {
      await editTrafficSource({
        variables: {
          trafficSource: source,
        },
      });
    }
    await refetch();
  };

  const handleDeleteClick = (id: string) => {
    setCurrentDeleteSourceId(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (currentDeleteSourceId) {
      await deleteTrafficSource({
        variables: {
          id: currentDeleteSourceId,
        },
      });
      await refetch();
      setDeleteModalOpen(false);
      setCurrentDeleteSourceId(null);
    }
  };

  const renderPlatformIcon = (platform: string) => (
    <span className={styles.platform}>
      {platform.toLowerCase() === "ios" ? <AppleIcon /> : <AndroidIcon />}
    </span>
  );

  const columns = [
    {
      title: "Order Type",
      dataIndex: "orderType",
      key: "orderType",
    },
    {
      title: "Platform",
      dataIndex: "platform",
      key: "platform",
      render: (platform: string) => renderPlatformIcon(platform),
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (price: number) => `$${price}`,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Status",
      dataIndex: "reservationStatus",
      key: "reservationStatus",
      render: (status: string) => (
        <span
          className={`${styles.status} ${
            status === "RESERVE" ? styles.statusReserve : ""
          }`}
        >
          {status}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: TrafficSource) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "1",
                label: "Edit",
                onClick: () => handleEdit(record),
              },
              {
                key: "2",
                label: "Delete source",
                onClick: () => handleDeleteClick(record.id || ""),
              },
            ],
          }}
          trigger={["click"]}
        >
          <Button type="text" icon={<EllipsisOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <Page
      title="Traffic sources"
      actions={
        <BasicButton
          onClick={handleAdd}
          title="Add source"
          variant="primary"
          icon={plusIcon}
        />
      }
    >
      <Table
        columns={columns}
        dataSource={data?.getTrafficSources}
        rowKey="id"
        loading={loading}
      />

      <TrafficSourceModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        initialData={currentSource}
        mode={modalMode}
      />

      <Modal
        title="Delete source"
        open={deleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        width={480}
        footer={[
          <div key="footer" className={dialog.footer}>
            <Button
              className={dialog.cancel}
              onClick={() => setDeleteModalOpen(false)}
            >
              Keep
            </Button>
            <Button className={dialog.delete} onClick={handleConfirmDelete}>
              Delete
            </Button>
          </div>,
        ]}
      >
        <p className={styles.confirm}>
          Are you sure you want to delete source?
        </p>
      </Modal>
    </Page>
  );
};

export default TrafficSourcesComponent;
